import type { MemoryStaffReasonCodeValue, StaffMemoryCommandOutcome } from "@lafayette/schemas";
import { type ReactNode, useState } from "react";

import { CommandDialog, type CommandFailure } from "../../ui/command-dialog";
import { SelectField } from "../../ui/fields";
import type { MemoryCommandResult } from "../commands";
import type { MemoryFailure } from "../errors";
import { reasonOptions } from "../labels";

// The shell of every memory command dialog: the kit `CommandDialog` with the command state of this page (`controller`). The command hook
// (`useMemoryCommands`) owns the Idempotency-Key and the reload rules; this file owns the three things the dialogs share: the closed
// reason select, the way a failure is shown, and the retry rule.
//
// A failure that cannot work again (a stale version, no access) ends the dialog: only Close is left, and the page behind has already
// read the memory again. A failure that may work on a second try (no answer, a busy server) keeps the form, LOCKS the fields and says
// "Try again": the command hook reuses the same Idempotency-Key for that second try, so the body must not change under it. A refused
// field (a 422) stays open and the message sits under the field.

export type Reason = MemoryStaffReasonCodeValue | "";

/** The state a dialog form needs around one command. */
export function useCommandForm(onDone: (outcome: StaffMemoryCommandOutcome) => void) {
  const [failure, setFailure] = useState<MemoryFailure | null>(null);
  const [pending, setPending] = useState(false);

  async function submit(run: () => Promise<MemoryCommandResult>) {
    setPending(true);
    setFailure(null);
    const result = await run();
    setPending(false);
    if (result.ok) onDone(result.outcome);
    else setFailure(result.failure);
  }

  return { failure, pending, submit };
}

/** A memory failure in the words of the kit dialog. A refused field keeps the form open; a conflict says the screen was stale. */
function toCommandFailure(failure: MemoryFailure): CommandFailure {
  return {
    message: failure.message,
    retryable: failure.retryable,
    keepForm: failure.kind === "invalid",
    tone: failure.kind === "conflict" ? "conflict" : "error",
    kind: failure.kind,
  };
}

export function ReasonSelect({
  value,
  onChange,
  error,
}: Readonly<{ value: Reason; onChange: (value: Reason) => void; error?: string | undefined }>) {
  return (
    <SelectField
      id="memory-reason"
      label="Reason"
      value={value}
      onChange={(next) => onChange(next as Reason)}
      options={reasonOptions}
      placeholder="Choose a reason"
      required
      error={error}
      hint="Pick the closest reason. It goes into the history. No free text is kept here."
    />
  );
}

type MemoryDialogProps = Readonly<{
  title: string;
  description?: ReactNode;
  confirmLabel: string;
  tone?: "primary" | "danger";
  children?: ReactNode;
  /** The failure to show as a message. `null` while a refused field shows its own message under the field. */
  failure: MemoryFailure | null;
  pending: boolean;
  /** Checks the fields and shows their errors. Return `false` to stop: the first invalid control takes focus. */
  validate: () => boolean;
  /** Runs when the person confirms and the fields are valid. */
  onSubmit: () => void;
  onClose: () => void;
  /** Classes for the panel, for example a wider one. */
  dialogClassName?: string | undefined;
}>;

export function MemoryDialog({
  title,
  description,
  confirmLabel,
  tone = "primary",
  children,
  failure,
  pending,
  validate,
  onSubmit,
  onClose,
  dialogClassName,
}: MemoryDialogProps) {
  return (
    <CommandDialog
      title={title}
      description={description}
      confirmLabel={confirmLabel}
      tone={tone}
      order="confirm-first"
      validate={validate}
      controller={{
        failure: failure === null ? null : toCommandFailure(failure),
        pending,
        submit: onSubmit,
      }}
      onClose={onClose}
      dialogClassName={dialogClassName}
    >
      {children}
    </CommandDialog>
  );
}
